"use client";

import { useEffect, useMemo, useState } from "react";
import ExpandedDetails from "../ExpandedDetails";
import { CountTimer } from "../../../commonComponents";
import { BorderedButton } from "../../../commonComponents";
import {
  getToken,
  getGameId,
  getAccountId,
} from "../../../../utils/localStorage";
import { useScreenWidth } from "../../../../utils/useScreenWidth";
import { Table, Popover, Checkbox, Slider, Radio, Spin } from "antd";
import { getLeagueImage } from "../../../../helpers/leagueLogo";
import { ReactComponent as FootballIcon } from "../../../../assets/images/football-icon.svg";
import { ReactComponent as BasketBallIcon } from "../../../../assets/images/basketball_icon.svg";
import {
  getPublicGamesForUser,
  getPublicGamesForPublic,
  getPublicGamesForUserFilter,
} from "../../../../utils/service";
import "./GameTable.scss";
import filter from "../../../../assets/images/Filter-icon.svg";
import start from "../../../../assets/images/sort_icon.png";

const stakeCheckboxOptions = [
  { label: "Sort Lowest to Highest", value: "low_to_high" },
  { label: "Sort Highest to Lowest", value: "high_to_low" },
];
const potCheckboxOptions = [
  { label: "Lowest - Highest", value: "low_to_high" },
  { label: "Highest - Lowest", value: "high_to_low" },
];
const deadlineCheckboxOptions = [
  { label: "Earliest - Latest", value: "latest" },
  { label: "Latest - Earliest", value: "oldest" },
];

const GameTable = () => {
  const [expandedRowsKey, setExpandedRowsKey] = useState([]);
  const [expandOnMobile, setExpandOnMobile] = useState({
    key: null,
    expand: false,
  });
  const [filterData, setFilterData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [gameTableData, setGameTableData] = useState();
  const [allGamesData, setAllGamesData] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const screenWidth = useScreenWidth();
  const isUser = getToken()
  const gameId = getGameId();
  const palyerId = getAccountId()
  const [noGame, setNoGame] = useState(null);
  const [loading, setLoading] = useState(true);

  // All derived from allGamesData (the unfiltered fetch), never from gameTableData
  // (which becomes the filtered result set once a filter is applied) — otherwise a
  // player filtering down to one result would watch the filters disappear and have
  // no way to undo their own filter.
  const hasEnoughGames = allGamesData.length >= 2; // Rule 1: outer gate
  const distinctSportCount = new Set(allGamesData.map((game) => game.sport_id)).size;
  const distinctLeagueCount = new Set(allGamesData.map((game) => game.season_id)).size;
  const hasMultipleSports = distinctSportCount >= 2; // Rule 2: Sport filter
  const hasMultipleLeagues = distinctLeagueCount >= 2; // Rule 2: League filter

  // League dropdown options: leagues actually present in this tab's games (allGamesData,
  // same unfiltered-source rule as above), deduped and sorted alphabetically. Previously
  // this list came from a separate "all leagues" backend endpoint (gameLeaguesData/
  // GET /leagues), which listed every league the platform has ever supported —
  // including dormant ones like NBA — regardless of whether any current game used it.
  // That endpoint is still used elsewhere (Insights, PrivateGame creation) and is left
  // untouched; this dropdown just stops reading from it.
  const leagueOptions = useMemo(() => {
    const names = new Set(
      allGamesData.map((game) => game.league_name).filter(Boolean)
    );
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allGamesData]);

  // If a previously-checked league drops out of leagueOptions (e.g. the underlying
  // game list refreshes), drop it from the active filter too, so we never send the
  // backend a competition[] value for a league that's no longer selectable — that
  // would silently zero out the results while the (now-invisible) checkbox still
  // reads as "active".
  useEffect(() => {
    setFilterData((prevFilterData) => {
      if (!prevFilterData?.competition?.length) return prevFilterData;
      const stillValid = prevFilterData.competition.filter((league) =>
        leagueOptions.includes(league)
      );
      if (stillValid.length === prevFilterData.competition.length) {
        return prevFilterData;
      }
      return { ...prevFilterData, competition: stillValid };
    });
  }, [leagueOptions]);

  const getGameTableData = async () => {
    setLoading(true);
    setNoGame(null);
    try {
      const response = isUser
        ? await getPublicGamesForUser(currentPage, palyerId, isUser)
        : await getPublicGamesForPublic(currentPage, palyerId, isUser);

      const games = isUser
        ? response?.games
        : response?.data?.games;

      const modifiedGameData = games?.map((game) => ({
        ...game,
        key: game.id,
      }));

      setGameTableData(modifiedGameData || []);
      setAllGamesData(modifiedGameData || []);
      if (!modifiedGameData || modifiedGameData.length === 0) {
        setNoGame(true);
      } else {
        setNoGame(false);
      }
    } catch (error) {
      setGameTableData([]);
      setNoGame(true);
    } finally {
      setLoading(false);
    }
  };

  const expandedRowRender = () => {
    return <ExpandedDetails expandedRowsKey={expandedRowsKey} />;
  };

  const handleMobileExpand = (key, value) => {
    setExpandOnMobile({
      key: key,
      expand: value,
    });
    if (value) {
      const matchedData = gameTableData?.find((item) => item.id === key);
      setExpandedRowsKey(matchedData);
    } else {
      setExpandedRowsKey([]);
    }
  };

  const handleSportsCheckbox = (checkedValues) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      sports: checkedValues,
    }));
  };
  const handleCompetitonsCheckbox = (checkedValues) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      competition: checkedValues,
    }));
  };
  const handleStakeValue = (e) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      stake: e.target.value,
    }));
  };
  const handlePotValue = (e) => {
    setFilterData((prevFilterData) => {
      const newFilterData = { ...prevFilterData };
      if ("stake" in newFilterData) {
        delete newFilterData.stake;
      }
      newFilterData.pot = e.target.value;
      return newFilterData;
    });
  };
  const handleDeadlineValue = (e) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      deadline: e.target.value,
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStakeSlider = (value) => {
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      stake_range: value,
    }));
  };
  const handleAfterStakeSlider = (value) => {
    if (value == 0) {
      setFilterData((prevFilterData) => ({
        ...prevFilterData,
      }));
    } else {
      setFilterData((prevFilterData) => ({
        ...prevFilterData,
        stake_range: value.toString(),
      }));
    }
  };
  const competitionsContent = (
    <div>
      <Checkbox.Group
        options={leagueOptions}
        onChange={handleCompetitonsCheckbox}
        className="game-table-checkbox"
      />
    </div>
  );
  const sportsContent = (
    <div>
      <Checkbox.Group
        onChange={handleSportsCheckbox}
        className="game-table-checkbox"
      >
        <Checkbox
          value="football"
          className="game-table-sports-checkboxes-item"
        >
          {/* <FootballIcon className="game-table-sports-checkboxes-image iconFootbll" /> */}
          Football
        </Checkbox>
        <Checkbox
          value="basketball"
          className="game-table-sports-checkboxes-item"
        >
          {/* <BasketBallIcon className="game-table-sports-checkboxes-image iconFootbll" /> */}
          Basketball
        </Checkbox>
      </Checkbox.Group>
    </div>
  );
  const deadlineContent = (
    <Radio.Group
      options={deadlineCheckboxOptions}
      onChange={handleDeadlineValue}
      className="game-table-stakeradio"
    />
  );
  const potContent = (
    <Radio.Group
      options={potCheckboxOptions}
      onChange={handlePotValue}
      className="game-table-stakeradio"
    />
  );
  const marks = {
    0: {
      style: {
        color: "#fff",
      },
      label: "$0",
    },
    500: {
      style: {
        color: "#fff",
      },
      label: <strong>$500+</strong>,
    },
  };
  const stakeContent = (
    <div>
      <Slider
        defaultValue={0}
        marks={marks}
        step={10}
        max={500}
        onChange={handleStakeSlider}
        onAfterChange={handleAfterStakeSlider}
        className="game-table-stake-slider"
      />
      <Radio.Group
        options={stakeCheckboxOptions}
        onChange={handleStakeValue}
        className="game-table-stakeradio"
      />
    </div>
  );

  const columns = [
    {
      title: "Game Ref",
      dataIndex: "title",
      width: "120px",
      render: (_, record) => {
        return (
          <div>
            {record.is_turbo && (
              <div className="game-table-turbotag font-cairo-bold">Turbo</div>
            )}
            <div>{record.title}</div>
          </div>
        );
      },
    },
    {
      title: hasEnoughGames && hasMultipleSports ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-sports"
          content={sportsContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Sport
            <img className="filterStyle" src={filter} />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Sport
        </div>
      ),
      dataIndex: "sport_id",
      render: (_, record) => {
        return (
          <div className="game-table-sports-cont">
            {record.sport_name.toLowerCase() == "football" ? (
              <FootballIcon className="game-table-sports-cont-icon" />
            ) : (
              <BasketBallIcon className="game-table-sports-cont-icon" />
            )}
            {record.sport_name}
          </div>
        );
      },
    },
    {
      title: hasEnoughGames && hasMultipleLeagues ? (
        <Popover
          content={competitionsContent}
          placement="bottom"
          trigger={["click"]}
          overlayClassName="game-table-popover-menu game-table-competitions"
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            League
            <img className="filterStyle" src={filter} />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          League
        </div>
      ),
      dataIndex: "competitions",
      render: (_, record) => {
        return (
          <div className="game-table-competitions-items">
            <div className="game-table-competitions-items-image">
              <img
                src={getLeagueImage(record.league_name, record.league_image)}
                alt="League Icon"
                className="game-table-competitions-items-icon"
                style={{ width: record?.sport_id == 5 ? "17px" : "34px" }}
              />
            </div>

            <div>{record.league_name}</div>
          </div>
        );
      },
    },
    {
      title: hasEnoughGames ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-stake"
          content={stakeContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Stake
            <img className="filterStyle" src={filter} alt="Filter Icon" />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Stake
        </div>
      ),
      dataIndex: "stake",
      render: (_, record) => {
        return (
          <div className="filterSpacing">
            {record.stake == "0.0" ? "Free*" : `$${Math.floor(record.stake).toLocaleString('en-US')}`}
          </div>
        );
      },
    },
    {
      title: hasEnoughGames ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-deadline"
          content={deadlineContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Starts In
            <img className="filterStyle" src={start} alt="Filter Icon" />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Starts In
        </div>
      ),
      dataIndex: "start_date",
      render: (_, record) => {
        return <CountTimer record={record.start_date} />;
      },
    },
    {
      title: hasEnoughGames ? (
        <Popover
          overlayClassName="game-table-popover-menu game-table-pot"
          content={potContent}
          placement="bottom"
          trigger={["click"]}
        >
          <div
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Pot
            <img className="filterStyle" src={start} alt="Filter Icon" />
          </div>
        </Popover>
      ) : (
        <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          Pot
        </div>
      ),
      dataIndex: "pot",
      render: (_, record) => {
        return <div>${Math.floor(record.pot).toLocaleString('en-US')}</div>;
      },
    },
    {
      title: "",
      dataIndex: "key",
      key: "key",
      render: (_, record) => {
        return (
          <div onClick={() => handleExpandToggle(record.key, record.is_joined)}>
            <BorderedButton
              name={record?.is_joined ? "Game Joined" : "Join Game"}
              link={"#"}
              btnHeight={"37px"}
              btnClass={"game-table-action-button"}
            />
          </div>
        );
      },
    },
  ];

  const handleExpandToggle = (key, is_joined) => {
    const isRowExpanded = expandedRowKeys.includes(key);
    if (isRowExpanded || is_joined) {
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
      setExpandedRowsKey([]);
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
      const matchedData = gameTableData?.find((item) => item.id === key);
      setExpandedRowsKey(matchedData);
    }
  };

  const handleFilteredGameData = async () => {
    const queryParams = Object.entries(filterData)
      .filter(([key, value]) => {
        return (
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === "string" && value !== "")
        );
      })
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const encodedArray = value
            .map((v) => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`)
            .join("&");
          return encodedArray;
        } else {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      })
      .join("&");
    const response = await getPublicGamesForUserFilter(
      currentPage,
      queryParams
    );
    if (response.games) {
      const modifiedGameData = response?.games?.map((game) => ({
        ...game,
        key: game.id,
      }));
      setGameTableData(modifiedGameData);
    }
  };
  useEffect(() => {
    getGameTableData();
  }, [currentPage, isUser]);

  useEffect(() => {
    if (filterData) {
      handleFilteredGameData();
    }
  }, [filterData]);

  return (
    <>
      {loading ? (
        <div className="game-table-loading">
          <Spin />
        </div>
      ) : !noGame ? (
        <>
          <div
            className={`${
              screenWidth >= 992 && "game-table container-border"
            } position-relative background-blur`}
          >
            {screenWidth >= 992 ? (
              <Table
                columns={columns}
                dataSource={gameTableData}
                expandable={{
                  expandedRowRender,
                  rowExpandable: (record) => !record.is_joined, // Prevent row expansion if game is joined
                  expandedRowKeys: expandedRowKeys,
                  onExpand: handleExpandToggle,
                }}
                onRow={(record) => ({
                  onClick: () => {
                    const newExpandedRowKeys = expandedRowKeys.includes(
                      record.key
                    )
                      ? []
                      : [record.key];
                    setExpandedRowKeys(newExpandedRowKeys);
                  },
                })}
                pagination={false}
                // pagination={{
                //   current: currentPage,
                //   total: gameTableData?.total_records,
                //   onChange: handlePageChange,
                // }}
              />
            ) : (
              <>
                {hasEnoughGames && (
                <div className="popOverContainerMain">
                  {hasMultipleSports && (
                  <div className="game-table-filter-grid game-table-filter-grid--single">
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-sports"
                        content={sportsContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Sport
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                  </div>
                  )}
                  {/* 2x2 grid: League, Stake, Starts In, Pot — all four cards share
                      game-table-filter-card's width (League's own current size,
                      the reference) and game-table-filter-grid's single gap value,
                      centered so no skewed corner clips the viewport. */}
                  <div className="game-table-filter-grid">
                    {hasMultipleLeagues && (
                    <div className="game-table-filter-card">
                      <Popover
                        content={competitionsContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                        overlayClassName="game-table-popover-menu game-table-competitions"
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          League
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    )}
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-stake"
                        content={stakeContent}
                        placement="left"
                        trigger={["click"]}
                        autoAdjustOverflow={false}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Stake
                          <img
                            className="filterStyle"
                            src={filter}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-deadline"
                        content={deadlineContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Starts In
                          <img
                            className="filterStyle"
                            src={start}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                    <div className="game-table-filter-card">
                      <Popover
                        overlayClassName="game-table-popover-menu game-table-pot"
                        content={potContent}
                        placement="bottomLeft"
                        trigger={["click"]}
                      >
                        <div
                          className="ant-dropdown-link"
                          onClick={(e) => e.preventDefault()}
                        >
                          Pot
                          <img
                            className="filterStyle"
                            src={start}
                            alt="Filter Icon"
                          />
                        </div>
                      </Popover>
                    </div>
                  </div>
                </div>
                )}
                <div className="gametable-mobile popOverMai">
                  {gameTableData?.map((item, index) => {
                    return (
                      <>
                        <div
                          className="gametable-mobile-title font-cairo-bold"
                          key={index + 1}
                        >
                          {item.title}
                        </div>
                        <div className="gametable-mobile-cont" key={index}>
                          <div className="gametable-mobile-cont-items">
                            {item?.is_turbo && (
                              <div className="game-table-turbotag font-cairo-bold">
                                Turbo
                              </div>
                            )}
                            <div className="gametable-mobile-cont-items-title">
                              League
                            </div>
                            <div className="gametable-mobile-cont-items-icon">
                              <img
                                src={getLeagueImage(item.league_name, item.league_image)}
                                alt="League Icon"
                                className="image-cover-full"
                              />
                            </div>
                            <div
                              className={
                                item?.is_turbo
                                  ? "gametable-mobile-cont-items-league-name-turbo"
                                  : undefined
                              }
                            >
                              {item.league_name}
                            </div>
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Sport
                            </div>
                            <div>{item.sport_name}</div>
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Stake
                            </div>
                            {/* <div>${Math.floor(item.stake)}</div>
                             */}
                            <div>
                              {item.stake == "0.0"
                                ? "Free*"
                                : `$${Math.floor(item.stake).toLocaleString('en-US')}`}
                            </div>
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Starts In
                            </div>
                            <CountTimer record={item.start_date} />
                          </div>
                          <div className="gametable-mobile-cont-items">
                            <div className="gametable-mobile-cont-items-title">
                              Pot
                            </div>
                            <div>${Math.floor(item.pot).toLocaleString('en-US')}</div>
                          </div>
                          {item.is_joined ? (
                            <div className="gametable-mobile-cont-items justify-center">
                              <BorderedButton
                                name={"Game Joined"}
                                link={"#"}
                                btnHeight={"54px"}
                                btnClass={"gametable-mobile-button"}
                              />
                            </div>
                          ) : (
                            <div
                              className="gametable-mobile-cont-items justify-center"
                              onClick={() =>
                                handleMobileExpand(
                                  item.id,
                                  !expandOnMobile.expand
                                )
                              }
                            >
                              <BorderedButton
                                name={"Join Game"}
                                link={"#"}
                                btnHeight={"54px"}
                                btnClass={"gametable-mobile-button"}
                              />
                            </div>
                          )}
                        </div>
                        {expandOnMobile.key == item.key &&
                          expandOnMobile.expand && (
                            <ExpandedDetails
                              expandedRowsKey={expandedRowsKey}
                            />
                          )}
                      </>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="game-table-nodata font-cairo-medium">
          No Games to Display.
        </div>
      )}
    </>
  );
};

export default GameTable;
